import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, LockSquare, Eye, EyeClosed, ShieldAlert, SparkSolid } from "iconoir-react";
import { api, saveActiveHousehold, requestMagicLink } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function formatError(detail) {
    if (!detail) return "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function Login() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const { setUser } = useAuth();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [showNameField, setShowNameField] = useState(false);
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [keepSigned, setKeepSigned] = useState(true);
    const [busy, setBusy] = useState(false);
    const [magicBusy, setMagicBusy] = useState(false);
    const [magicSent, setMagicSent] = useState(false);
    const [showPwForm, setShowPwForm] = useState(false);

    const handleMagicLink = async () => {
        if (!email.trim() || !/.+@.+\..+/.test(email.trim())) {
            toast.error("Enter a valid email first");
            return;
        }
        setMagicBusy(true);
        try {
            const inviteCode = sessionStorage.getItem("milo_pending_code") || undefined;
            // If we don't already have a name and we haven't yet asked, peek at the email
            if (!name.trim() && !showNameField) {
                const { data: chk } = await api.post("/auth/check-email", { email: email.trim() });
                if (chk?.is_new_user) {
                    setShowNameField(true);
                    setMagicBusy(false);
                    return;
                }
            }
            await requestMagicLink({
                email: email.trim(),
                name: name.trim() || undefined,
                invite_code: inviteCode,
            });
            setMagicSent(true);
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Couldn't send link");
        } finally {
            setMagicBusy(false);
        }
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/login", { email: email.trim(), password });
            if (data.session_token) localStorage.setItem("milo_session_token", data.session_token);
            setUser(data.user);
            const pendingCode = sessionStorage.getItem("milo_pending_code");
            if (pendingCode) {
                sessionStorage.removeItem("milo_pending_code");
                nav(`/picker?code=${pendingCode}`, { replace: true });
                return;
            }
            try {
                const { data: list } = await api.get("/me/households");
                if (list.length === 1) {
                    const e0 = list[0];
                    saveActiveHousehold({
                        household_id: e0.household.id,
                        household_name: e0.household.name,
                        household_code: e0.household.code,
                        member_id: e0.member.id,
                        member_name: e0.member.name,
                    });
                    nav("/app", { replace: true });
                    return;
                }
            } catch (e) { console.warn("pages/Login.jsx:", e); }
            nav("/picker", { replace: true });
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    if (magicSent) {
        return (
            <div className="auth-bg min-h-screen w-full flex items-center justify-center p-5">
                <div className="auth-sun" />
                <div className="auth-heart" />
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative z-10 w-full max-w-md"
                >
                    <div className="flex flex-col items-center text-center mb-6">
                        <img src="/milo-logo.png" alt="Milo" className="h-14 w-auto object-contain mb-6" />
                    </div>
                    <div className="bg-white/80 backdrop-blur-xl rounded-[28px] p-6 sm:p-7 shadow-[0_2px_4px_rgba(13,42,27,0.04),0_24px_48px_rgba(13,42,27,0.08)] border border-white/40 text-center">
                        <div className="w-12 h-12 rounded-full bg-[#e8f1e4] flex items-center justify-center mx-auto mb-3">
                            <Mail className="w-6 h-6 text-[#2F6B43]" />
                        </div>
                        <div className="font-display text-3xl font-bold tracking-tight">Check your inbox</div>
                        <div className="text-sm text-[#255636] mt-3">
                            We sent a link to <b>{email}</b>. Tap it and you're in. The link works for 20 minutes.
                        </div>
                        <div className="text-xs text-[#5b6d63] mt-4">
                            Didn't see it? Check spam, or{" "}
                            <button
                                onClick={() => { setMagicSent(false); }}
                                className="font-semibold text-[#2F6B43] underline"
                                data-testid="magic-try-again-btn"
                            >
                                try again
                            </button>
                            .
                        </div>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="auth-bg min-h-screen w-full flex items-center justify-center p-5">
            <div className="auth-sun" />
            <div className="auth-heart" />
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="relative z-10 w-full max-w-md"
            >
                <div className="flex flex-col items-center text-center mb-6">
                    <img src="/milo-logo.png" alt="Milo" className="h-14 w-auto object-contain mb-6" />
                    <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[1.02]">
                        Welcome back!
                    </h1>
                    <p className="text-base text-[#255636] mt-3">Happy to see you again 👋</p>
                    <p className="text-sm text-[#5b6d63] mt-3">
                        New here?{" "}
                        <Link
                            to={params.get("code") ? `/signup?code=${params.get("code")}` : "/signup"}
                            className="font-bold text-[#2F6B43] underline decoration-2 underline-offset-2"
                            data-testid="goto-signup-link"
                        >
                            Create account
                        </Link>
                    </p>
                </div>

                <div className="bg-white/80 backdrop-blur-xl rounded-[28px] p-6 sm:p-7 shadow-[0_2px_4px_rgba(13,42,27,0.04),0_24px_48px_rgba(13,42,27,0.08)] border border-white/40 space-y-4">
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-[#e8f1e4] flex items-center justify-center">
                                <Mail className="w-3.5 h-3.5 text-[#2F6B43]" />
                            </div>
                            <span className="text-sm font-semibold text-[#0d2a1b]">Email</span>
                        </div>
                        <input
                            data-testid="login-email-input"
                            type="email"
                            required
                            className="brut-input"
                            placeholder="you@family.com"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (showNameField) setShowNameField(false);
                            }}
                            autoFocus
                        />
                    </div>

                    {showNameField && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            data-testid="login-name-section"
                        >
                            <div className="mb-2 flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-[#fde9c4] flex items-center justify-center">
                                    <SparkSolid className="w-3.5 h-3.5 text-[#a47b1f]" />
                                </div>
                                <span className="text-sm font-semibold text-[#0d2a1b]">Welcome — what should we call you?</span>
                            </div>
                            <input
                                data-testid="login-name-input"
                                type="text"
                                className="brut-input"
                                placeholder="Your first name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoFocus
                            />
                            <div className="text-xs text-[#5b6d63] mt-1.5">First time signing in. We'll create your account.</div>
                        </motion.div>
                    )}

                    <button
                        type="button"
                        onClick={handleMagicLink}
                        disabled={magicBusy}
                        data-testid="magic-link-btn"
                        className="w-full flex items-center justify-center gap-2 bg-[#2F6B43] text-white rounded-2xl py-3.5 font-semibold text-base hover:bg-[#255636] transition-colors shadow-[0_6px_20px_rgba(47,107,67,0.3)] disabled:opacity-70"
                    >
                        <SparkSolid className="w-4 h-4" />
                        {magicBusy ? "Sending…" : "Email me a sign-in link"}
                    </button>

                    {!showPwForm ? (
                        <button
                            type="button"
                            onClick={() => setShowPwForm(true)}
                            data-testid="use-password-btn"
                            className="w-full text-center text-sm font-semibold text-[#5b6d63] hover:text-[#2F6B43]"
                        >
                            Or use a password instead
                        </button>
                    ) : (
                        <form onSubmit={submitPassword} className="space-y-3 pt-2 border-t border-[#0d2a1b]/5">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-[#e8f1e4] flex items-center justify-center">
                                            <LockSquare className="w-3.5 h-3.5 text-[#2F6B43]" />
                                        </div>
                                        <span className="text-sm font-semibold text-[#0d2a1b]">Password</span>
                                    </div>
                                </div>
                                <div className="relative">
                                    <input
                                        data-testid="login-password-input"
                                        type={showPw ? "text" : "password"}
                                        required
                                        className="brut-input pr-11"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw((s) => !s)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center opacity-60 hover:opacity-100"
                                    >
                                        {showPw ? <EyeClosed className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    data-testid="keep-signed-in-checkbox"
                                    checked={keepSigned}
                                    onChange={(e) => setKeepSigned(e.target.checked)}
                                    className="w-5 h-5 rounded-[6px] accent-[#2F6B43]"
                                />
                                <span className="text-sm font-medium text-[#0d2a1b]">Keep me signed in</span>
                            </label>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="login-submit-btn"
                                className="w-full flex items-center justify-center gap-2 bg-white border border-[#0d2a1b]/10 text-[#0d2a1b] rounded-2xl py-3 font-semibold text-sm hover:bg-[#faf5ea] transition-colors disabled:opacity-70"
                            >
                                {busy ? "Signing in…" : "Sign in with password"} <ArrowRight className="w-4 h-4" />
                            </button>
                        </form>
                    )}
                </div>

                <div className="mt-5 flex items-center justify-center gap-2 text-xs text-[#5b6d63]">
                    <div className="w-8 h-8 rounded-full bg-[#e8f1e4] flex items-center justify-center">
                        <ShieldAlert className="w-4 h-4 text-[#2F6B43]" />
                    </div>
                    <div>
                        Your family's data is safe and private.
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
